import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LockIcon } from 'lucide-react';
import { AdminBarChart, DistributionList } from '../../components/admin/AdminChart';
import { AdminMetricCard, AdminPageTitle, AdminTabs, ConfirmationModal, Panel, StatusBadge } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { adminBadges, levelDistribution, progressionRules, recordAudit, streakBuckets, xpDistribution, type AdminBadge } from '../../data/admin';
import { useLocale } from '../../contexts/LocaleContext';
import { ConfigParamsPanel } from '../../components/admin/ConfigParamsPanel';
import { FixedRulesPanel } from '../../components/admin/FixedRulesPanel';
import { ChallengeAdmin } from '../../components/admin/ChallengeAdmin';
import { engagementAdminSections, recapAdminParams, recapArchetypeCopyNote, type EngagementAdminSection } from '../../data/engagementAdmin';
import { setRecapParam, useRecapConfig } from '../../hooks/recapConfigStore';
import { hasCapability, prototypeAdminRole } from '../../data/adminOperations';
const tabs = [{
  id: 'xp',
  label: 'Bee'
}, {
  id: 'niveis',
  label: 'Níveis'
}, {
  id: 'sequencias',
  label: 'Sequências'
}, {
  id: 'medalhas',
  label: 'Medalhas'
}, {
  id: 'desafios',
  label: 'Desafios'
}, {
  id: 'protecao',
  label: 'Proteção de Sequência'
}, {
  id: 'colmeia',
  label: 'Minha Colmeia'
}, {
  id: 'bau',
  label: 'Baú da Colmeia'
}, {
  id: 'liga',
  label: 'Liga'
}, {
  id: 'retrospectiva',
  label: 'Retrospectiva'
}];
const engagementTabs: Record<string, EngagementAdminSection> = {
  protecao: 'protection',
  colmeia: 'hive',
  bau: 'chest',
  liga: 'league',
  retrospectiva: 'recap'
};
export function AdminGamification() {
  const {
    tab: param
  } = useParams();
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const tab = tabs.some(item => item.id === param) ? param as string : 'xp';
  const [editing, setEditing] = useState<AdminBadge | null>(null);
  // Same in-memory recap config the Mobile recap reads.
  const recapConfig = useRecapConfig();
  const isRecap = engagementTabs[tab] === 'recap';
  const badgeColumns: Column<AdminBadge>[] = [{
    id: 'name',
    header: 'Medalha',
    sortValue: row => row.name,
    cell: row => <span className="text-[13px] font-medium text-deep" data-id="element-1713" data-mp="1lg43kbrzlzh7">{row.name}</span>
  }, {
    id: 'rule',
    header: 'Regra de qualificação',
    width: '34%',
    cell: row => <span className="text-[12.5px] text-muted" data-id="element-1714" data-mp="1bhep6szaux5p">{tl(row.rule)}</span>
  }, {
    id: 'unlocked',
    header: 'Desbloqueios',
    align: 'right',
    sortValue: row => Number(row.unlocked.replace(/\./g, '')),
    cell: row => <span className="bee-num font-bold text-deep" data-id="element-1715" data-mp="35v5rt8j7ogrn">{row.unlocked}</span>
  }, {
    id: 'status',
    header: 'Status',
    cell: row => <StatusBadge status={row.status} label={row.status === 'active' ? 'Ativa' : 'Rascunho'} data-id="element-1716" data-mp="39dv2htrhx3sg" />
  }, {
    id: 'created',
    header: 'Criada em',
    sortValue: row => row.created,
    cell: row => <span className="bee-num text-muted" data-id="element-1717" data-mp="3i5nei8w64kz5">{row.created}</span>
  }];
  return <div className="space-y-4" data-id="element-1718" data-mp="3n5jelx1qvs9f">
      <AdminPageTitle title="Gamificação" subtitle="Distribuição de Bee, níveis, sequências, medalhas e recursos de engajamento. Regras fixas do produto são somente leitura; parâmetros configuráveis exigem permissão." data-id="element-1719" data-mp="3n3owsrlrsg9k" />

      <AdminTabs tabs={tabs} active={tab} onChange={next => navigate(`/admin/gamificacao/${next}`)} data-id="element-1720" data-mp="2zffc5t0alyk0" />

      {tab === 'xp' && <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1721" data-mp="34fjyhznvtni5">
            <AdminMetricCard label="Bee médio por usuário" value="74,2" delta={0.9} accent data-id="element-1722" data-mp="eppz9lj5gmty" />
            <AdminMetricCard label="Usuários em 100 Bee hoje" value="31.208" delta={6.7} data-id="element-1723" data-mp="jo1ao0p3f5ea" />
            <AdminMetricCard label="Acima do gate de 50 Bee" value="81.040" delta={4.2} data-id="element-1724" data-mp="1sl86vetdpkh9" />
            <AdminMetricCard label="Bônus de foto usados" value="12.744" delta={2.8} data-id="element-1725" data-mp="1sn2p2ld9myhn" />
          </section>

          <AdminBarChart title="Distribuição de Bee no dia" question="Quantos usuários param antes do gate de 50 Bee?" data={xpDistribution} xKey="range" series={[{
        id: 'users',
        name: 'Usuários'
      }]} data-id="element-1726" data-mp="21lwki8ev1itm" />

          <Panel title="Regras de progressão" hint="Configuração vigente, em modo somente leitura." data-id="element-1727" data-mp="2bi98cpnq52gg">
            <ul className="space-y-2" data-id="element-1728" data-mp="26iwq6i7ssjk7">
              {progressionRules.map(rule => <li key={rule.id} className="flex items-center justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-1729" data-mp="2kbmbsljezmgs">
                  <span className="text-[12.5px] text-deep" data-id="element-1730" data-mp="2uav3f7qt4ozz">{tl(rule.label)}</span>
                  <span className="flex items-center gap-2" data-id="element-1731" data-mp="2pctisidqghme">
                    <span className="bee-num text-[12.5px] font-bold text-deep" data-id="element-1732" data-mp="38xk3pbsvv67f">
                      {rule.value}
                    </span>
                    <LockIcon size={13} className="text-muted" aria-hidden="true" data-id="element-1733" data-mp="3dvvb7fvph4mh" />
                  </span>
                </li>)}
            </ul>
            <p className="mt-3 text-[11.5px] leading-snug text-muted" data-id="element-1734" data-mp="33xo73xmimh1w">
              A base de 100 Bee, o gate de 50 Bee e o bônus de +10 Bee fora da base são regras
              críticas do produto. A edição exige aprovação de Super Admin e não é exposta
              aqui.
            </p>
          </Panel>
        </>}

      {tab === 'niveis' && <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-1735" data-mp="2g6ihwruoqcex">
          <AdminBarChart title="Usuários por nível" question="A base está avançando de nível ou concentrada no início?" data={levelDistribution} xKey="level" series={[{
        id: 'users',
        name: 'Usuários'
      }]} data-id="element-1736" data-mp="2l8r9pkvtn8fg" />
          <Panel title="Proporção por nível" data-id="element-1737" data-mp="hrbb16odbl0k">
            <DistributionList items={levelDistribution.map(level => ({
          label: level.level,
          value: level.users,
          secondary: level.users.toLocaleString('pt-BR')
        }))} data-id="element-1738" data-mp="hs47netm4u4z" />
          </Panel>
        </div>}

      {tab === 'sequencias' && <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1739" data-mp="10pf5qw3idhiu">
            <AdminMetricCard label="Sequências ativas" value="31.208" delta={6.7} accent data-id="element-1740" data-mp="10nkte66ayxqd" />
            <AdminMetricCard label="Média da sequência ativa" value="5,4 dias" delta={1.2} data-id="element-1741" data-mp="vohpwq5z0zki" />
            <AdminMetricCard label="Quebras ontem" value="2.917" delta={-3.1} data-id="element-1742" data-mp="qpocexqop8kc" />
            <AdminMetricCard label="Acima de 30 dias" value="228" delta={9.4} data-id="element-1743" data-mp="mp4fnva0w6yq" />
          </section>
          <AdminBarChart title="Sequências por faixa" question="Onde as sequências costumam quebrar?" data={streakBuckets} xKey="bucket" series={[{
        id: 'users',
        name: 'Usuários'
      }]} horizontal data-id="element-1744" data-mp="3bihzcj7z4a8z" />
        </>}

      {tab === 'medalhas' && <>
          <DataTable rows={adminBadges} columns={badgeColumns} rowKey={row => row.id} pageSize={10} actions={[{
        id: 'edit',
        label: 'Editar regra',
        onSelect: setEditing
      }]} data-id="element-1745" data-mp="1qxbhv16pxtib" />
          <Panel title="Edição de medalhas" data-id="element-1746" data-mp="25oapo9zqce0u">
            <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-1747" data-mp="2an5i83ni1gy5">
              Alterar a regra de uma medalha muda a qualificação de toda a base. Por isso a
              edição exige confirmação explícita e fica registrada na auditoria com a regra
              anterior e a nova.
            </p>
          </Panel>
        </>}

      {tab === 'desafios' && <ChallengeAdmin data-id="element-1748" data-mp="kdbzwso973l7" />}

      {engagementTabs[tab] && <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr_1.4fr]" data-id="element-1749" data-mp="pbdilchrp0w5">
          <div className="space-y-3" data-id="element-1750" data-mp="2r7mab6m1g7w6">
            <FixedRulesPanel rules={engagementAdminSections[engagementTabs[tab]].fixedRules} data-id="element-1751" data-mp="2r6jkl20gcbhu" />
            {isRecap && <Panel title="Texto dos tipos de abelha" data-id="element-1752" data-mp="2n46jiqwnh1re">
                <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-1753" data-mp="30wmdz3bhjrgp">{tl(recapArchetypeCopyNote)}</p>
                <p className="mt-2 text-[12px] leading-relaxed text-muted" data-id="element-1754" data-mp="2vzm9gwuaawrp">
                  Protótipo: alterações confirmadas aqui atualizam na hora a retrospectiva do app,
                  que lê a mesma configuração em memória. Elas voltam ao padrão ao recarregar —
                  persistência e versionamento são Marco 3.
                </p>
              </Panel>}
          </div>
          <ConfigParamsPanel key={tab} section={tl(engagementAdminSections[engagementTabs[tab]].title)} params={isRecap ? recapAdminParams(recapConfig) : engagementAdminSections[engagementTabs[tab]].params} canEdit={hasCapability(prototypeAdminRole, 'engagement.manageConfig')} onApply={isRecap ? setRecapParam : undefined} data-id="element-1755" data-mp="2d71riag4ic2o" />
        </div>}

      <ConfirmationModal open={editing !== null} title={`Editar a regra de ${editing?.name ?? ''}?`} body="A nova regra revalida a qualificação de toda a base, o que pode conceder ou remover a medalha de usuários já atendidos. A alteração fica registrada na auditoria." confirmLabel="Entendi, continuar" onConfirm={() => {
      if (editing) {
        recordAudit({
          action: {
            'pt-BR': 'Editou regra de medalha',
            'en-US': 'Edited badge rule'
          },
          entity: `badge:${editing.id}`,
          context: {
            'pt-BR': editing.name,
            'en-US': editing.name
          },
          before: tl(editing.rule),
          severity: 'sensitive'
        });
      }
      setEditing(null);
    }} onClose={() => setEditing(null)} data-id="element-1756" data-mp="1fg55gjy0u1rv" />
    </div>;
}